/* eslint-disable max-len */
import {
  Show,
  SimpleShowLayout,
  ReferenceField,
  TextField,
  NumberField,
  DateField,
  useTranslate,
  useShowContext,
  Labeled,
  FunctionField
} from "react-admin";
import { capitalize } from "underscore.string";
import { GoogleMap, Marker } from "@react-google-maps/api";
import { Chip } from "@mui/material";
import { EditButtonIcon, LocationDetailsIcon } from "assets/Icons";
import CustomHeaderActions from "./CustomHeaderActions";
import ShowAndEditDetails from "./ShowAndEditDetails";
import Gallery from "./Gallery";
import GalleryImage from "./GalleryImage";
import { amenitiesNameHelper } from "utils/helpers";
import { Link } from "react-router-dom";
import { CustomButton } from "./CustomButton";
import { getPhone } from "./PhoneInput";

function HoursShow() {
  const { record } = useShowContext();
  const translate = useTranslate();

  return (
    <>
      <h5>{translate("locations.hours")}</h5>
      {days.map((day: string, i: number) => {
        if (!record?.[`${day}_hours_open`] || !record?.[`${day}_hours_close`]) {
          return (
            <div key={i}>
              <p>{capitalize(translate(`locations.${day}`))}</p>
              <p>N/A</p>
            </div>
          );
        }
        const options: Intl.DateTimeFormatOptions = {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true
        };
        if (record?.timezone) {
          options.timeZone = record.timezone;
        }
        return (
          <div key={i}>
            <p>{capitalize(translate(`locations.${day}`))}</p>
            <DateField
              source={`${day}_hours_open`}
              label={translate("locations.open")}
              showDate={false}
              showTime={true}
              options={options}
            />
            {" - "}
            <DateField
              source={`${day}_hours_close`}
              label={translate("locations.close")}
              showDate={false}
              showTime={true}
              options={options}
            />
          </div>
        );
      })}
    </>
  );
}

const ConditionalTextField = ({
  sourceName
}: {
  sourceName: string;
  label?: any;
}) => {
  const { record } = useShowContext();
  const translate = useTranslate();

  if (sourceName === "amenities_enums") {
    if (record?.amenities_enums?.length > 0) {
      return (
        <>
          <p className="text-[14px] text-mono-secondaryTextColor font-['Inter']">
            {translate("locations.amenities")}
          </p>
          <div className="flex gap-2 flex-wrap">
            {record?.amenities_enums.map((item: string, index: number) => (
              <Chip
                key={index}
                label={
                  amenitiesNameHelper[item as keyof typeof amenitiesNameHelper]
                }
              />
            ))}
          </div>
        </>
      );
    } else {
      return null;
    }
  }

  if (sourceName === "libphonenumber") {
    return (
      <Labeled label={translate("locations.phone_number")}>
        <FunctionField
          render={(record: any) => getPhone(record?.libphonenumber)}
        />
      </Labeled>
    );
  }

  if (sourceName === "smslibphonenumber" && record?.send_sms) {
    return (
      <Labeled label={translate("locations.sms_phone_number")}>
        <FunctionField
          render={(record: any) => getPhone(record?.smslibphonenumber)}
        />
      </Labeled>
    );
  }

  if (record?.[sourceName]) {
    return (
      <Labeled label={translate(`locations.${sourceName}`)}>
        <TextField source={sourceName} />
      </Labeled>
    );
  }

  return null;
};

const LocationShowCustomButtons = () => {
  const { record } = useShowContext();
  const translate = useTranslate();

  if (record?.id) {
    return (
      <div className="flex gap-3 sm:gap-2 flex-wrap w-full sm:w-auto">
        <Link
          to={`${process.env.REACT_APP_CUSTOMER_APP_URL}/location/${record.slug}`}
          style={{ width: "inherit" }}
          target="_blank"
        >
          <CustomButton
            variant="secondary"
            text={translate("locations.view_location_page")}
            sx={{
              "@media screen and (max-width:640px)": {
                width: "100%"
              }
            }}
          />
        </Link>
        <Link
          to={`/dashboard/Location/${record.id}`}
          style={{ width: "inherit" }}
        >
          <CustomButton
            text={translate("common.edit")}
            variant="secondary"
            startIcon={<EditButtonIcon />}
            sx={{
              "@media screen and (max-width:640px)": {
                width: "100%"
              }
            }}
          />
        </Link>
      </div>
    );
  }
  return null;
};

export default function LocationShow() {
  const translate = useTranslate();

  return (
    <Show
      actions={<CustomHeaderActions title={translate("pagenames.location")} />}
    >
      <SimpleShowLayout>
        <ShowAndEditDetails
          title={translate("locations.location_details")}
          view="show"
          icon={<LocationDetailsIcon />}
          marginBottom="0px"
          customButtons={<LocationShowCustomButtons />}
        />
        <Gallery title={translate("locations.cover_photo")}>
          <GalleryImage height="420px" width="745px" source="cover_photo_url" />
        </Gallery>
        <ReferenceField
          source="menu_id"
          reference="Menu"
          label={translate("locations.menu")}
        />
        <TextField source="name" label={translate("locations.name")} />
        <TextField
          source="nickname"
          label={translate("locations.nickname")}
          emptyText="-"
        />
        <TextField
          source="visible_on_homepage"
          label={translate("locations.visible_on_homepage")}
        />
        <ConditionalTextField sourceName="description" />
        <ConditionalTextField sourceName="amenities_enums" />
        <ConditionalTextField label={false} sourceName="facebook_url" />
        <ConditionalTextField label={false} sourceName="instagram_url" />
        <ConditionalTextField label={false} sourceName="twitter_url" />
        <ConditionalTextField label={false} sourceName="website_url" />
        <NumberField
          source="multi_person"
          label={translate("locations.multi_person")}
        />
        <ConditionalTextField sourceName="libphonenumber" />
        <ConditionalTextField sourceName="smslibphonenumber" />
        <div className="w-full mb-4">
          <GoogleMapHydrated />
        </div>
        <TextField source="timezone" />
        <HoursShow />
      </SimpleShowLayout>
    </Show>
  );
}

export const GoogleMapHydrated = ({ label }: { label?: string }) => {
  const { record } = useShowContext();

  if (!record?.address_latitude || !record?.address_longitude) {
    return null;
  }

  return (
    <>
      <span className="ra-field ra-field-multi_person RaSimpleShowLayout-row css-8l66u2-MuiStack-root-RaLabeled-root">
        <p className="MuiTypography-root MuiTypography-body1 RaLabeled-label css-1pnmrwp-MuiTypography-root">
          <span>
            {label || "Address"}: {record?.address_display}
          </span>
        </p>
      </span>
      <GoogleMap
        zoom={13}
        mapContainerStyle={{ height: 300, width: "50%" }}
        center={{
          lat: record?.address_latitude,
          lng: record?.address_longitude
        }}
      >
        <Marker
          position={{
            lat: record?.address_latitude,
            lng: record?.address_longitude
          }}
        />
      </GoogleMap>
    </>
  );
};

const days = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday"
];
